import { RiveByKey } from "@components/riveAnimation";
import { Box, Typography } from "@mui/material";
import { ArcElement, BarController, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from "chart.js";
import { Bar, Pie } from "../../../theme/charts";
import { LoadingCard } from "../../common/LoadingCard";
import { colors } from "../../ui/colors";
import { WidgetBenefitBlockedState, getBenefitErrorMessage, isBenefitForbiddenError } from "../../common/WidgetBenefitBlockedState";

ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, BarController);

export function QuotesChart({
  overview,
  metric = "quantity", // "quantity" | "earning" | "percent"
  period = "monthly",
  isLoading = false,
  error = null,
  isBlurred = false,
  forceBarMode = false,
}) {
  if (isLoading) {
    return (
      <Box
        sx={{
          mt: 2,
          height: 180,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LoadingCard />
      </Box>
    );
  }

  if (error) {
    const isForbidden = isBenefitForbiddenError(error);
    if (isForbidden) {
      return (
        <WidgetBenefitBlockedState
          title="Quotes Analytics Locked"
          message={getBenefitErrorMessage(error, "Your current plan does not include quote analytics.")}
          height="100%"
        />
      );
    }

    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 180, width: "100%", px: 2 }}>
        <Typography color="error" align="center" variant="body2" sx={{ fontWeight: 500 }}>
          Error loading quotes
        </Typography>
      </Box>
    );
  }

  const metricKeyMap = {
    earning: "sales",
    percent: "percentage",
    quantity: "quantity",
  };

  const realMetric = metricKeyMap[metric] || "quantity";

  const residentialData = overview?.residential?.[period]?.[realMetric];
  const commercialData = overview?.commercial?.[period]?.[realMetric];

  let combinedData = { revision: 0, approved: 0, declined: 0 };

  if (metric === "quantity" || metric === "earning") {
    combinedData = {
      revision: (residentialData?.revision || 0) + (commercialData?.revision || 0),
      approved: (residentialData?.approved || 0) + (commercialData?.approved || 0),
      declined: (residentialData?.declined || 0) + (commercialData?.declined || 0),
    };
  }

  if (metric === "percent") {
    combinedData = {
      revision: ((residentialData?.revision || 0) + (commercialData?.revision || 0)) / 2,
      approved: ((residentialData?.approved || 0) + (commercialData?.approved || 0)) / 2,
      declined: ((residentialData?.declined || 0) + (commercialData?.declined || 0)) / 2,
    };
  }

  const chartValues = Object.values(combinedData);
  const hasData = chartValues.some((val) => val > 0);

  const periodLabels = {
    daily: "today",
    weekly: "this week",
    monthly: "this month",
    quarterly: "this quarter",
    yearly: "this year",
  };

  const metricLabels = {
    earning: "earnings",
    percent: "percentages",
    quantity: "quotes",
  };

  if (!hasData) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{}}>
        {/* <Lottie options={lottieOptions} height={100} width={100} isClickToPauseDisabled /> */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
            marginTop: -8,
          }}
        >
          <RiveByKey riveKey="timesheets" boxSize={200} />
        </Box>
        <Typography variant="body2" sx={{ display: "flex", mt: -2, color: "text.secondary", textAlign: "center", fontSize: "0.85rem", opacity: 0.8 }}>
          No quotes registered yet
        </Typography>
      </Box>
    );
  }

  const labelsMap = {
    revision: "Revision",
    approved: "Approved",
    declined: "Declined",
  };

  const chartLabels = Object.keys(combinedData).map((key) => labelsMap[key] || key);

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Quotes",
        data: chartValues,
        backgroundColor: [
          "#317df7", // Revision
          "#19ffb2", // Approved
          "#ef4444", // Declined
        ],
        borderColor: [colors.gray[50], colors.gray[50], colors.gray[50]],
        borderWidth: 6,
        barThickness: 80, // ancho
        maxBarThickness: 120, // evita que crezcan demasiado
        borderRadius: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    cutout: "0%",
    plugins: {
      legend: {
        position: "left",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 10,
          boxHeight: 10,
          padding: 12,
          font: { size: 11 },
          generateLabels: (chart) => {
            const dataset = chart.data.datasets[0];
            return chart.data.labels.map((label, i) => {
              const value = dataset.data[i];
              let displayValue = value;

              if (metric === "earning") displayValue = `$${value.toLocaleString()}`;
              else if (metric === "percent") displayValue = `${(Number(value) || 0).toFixed(2)}%`;
              else displayValue = `${value} quotes`;

              return {
                text: `${label} — ${displayValue}`,
                fillStyle: dataset.backgroundColor[i],
                strokeStyle: dataset.borderColor[i],
                pointStyle: "circle",
              };
            });
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.raw;
            if (metric === "earning") return `$${value.toLocaleString()}`;
            if (metric === "percent") return `${(Number(value) || 0).toFixed(2)}%`;
            return `${value} quotes`;
          },
        },
      },
    },
  };

  // Opciones específicas para cuando sea una barra (isLargeSlot)
  const barOptions = {
    indexAxis: "x",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
    scales: {
      x: { grid: { display: false } },
      y: { display: false, grid: { display: false } }, // Ocultamos Y para limpiar la vista
    },
  };

  return (
    <Box
      sx={{
        mt: 1,
        width: "100%",

        height: forceBarMode ? 250 : 180,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        maxWidth: forceBarMode ? "100%" : "350px",
        margin: "0 auto", // Centrado horizontal
      }}
    >
      {forceBarMode ? (
        <Bar key="bar-chart" data={chartData} options={barOptions} />
      ) : (
        <Pie
          key="pie-chart"
          data={chartData}
          options={{
            ...options,
            maintainAspectRatio: false,
            plugins: {
              ...options.plugins,
              legend: {
                ...options.plugins.legend,

                position: "left",
                labels: {
                  ...options.plugins.legend.labels,
                  boxWidth: 8,
                  padding: 8,
                  font: { size: 10 },
                },
              },
            },
          }}
        />
      )}
    </Box>
  );
}
